import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Cross, MoreVertical, Pencil, Printer, Ticket as TicketLogo, Trash2, X } from "lucide-react";
import { Dialog, DialogTrigger } from "@/components/ui/Dialog";
import React, { useEffect, Suspense } from "react";
import { useToast } from "@/components/ui/UseToast";
import { ErrorType } from "@/app/types";
import warning from "@/assets/warning.svg";
import { TicketStatus, useCancelTicketMutation, useDeleteTicketMutation, useLazyGetTicketAuditDetailQuery } from "@/services/LiveQueueService";
import { AuditLog, Ticket } from "@/app/types/tickets";
import { isCancel } from "axios";
import DetailLogs from "../ticket-detail/AuditLog";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
export function DataTableRowActions({
  data,
  refetch,
  handleEdit,
  access,
  setTicketId,
  setOpenLog,
  setSheetOpen,
  setSelectedTicket,
  handleBrowserPrint
}: Readonly<{
  data: any;
  refetch?: any;
  handleEdit?: any;
  access: string;
  setTicketId: any;
  setOpenLog: any;
  setSheetOpen: any;
  setSelectedTicket: any;
  handleBrowserPrint: (ticket: Ticket) => void;
}>) {
  const { userInfo } = useSelector((state: RootState) => state.auth);

  const [isdelete, setIsDelete] = React.useState(false);
  const [iscancel, setIsCancel] = React.useState(false);
  const { toast } = useToast();
  const [deleteTicket] = useDeleteTicketMutation();
  const [cancelTicket] = useCancelTicketMutation();

  const deleteRow = async () => {
    try {
      const resp = await deleteTicket([data?.id])
        .unwrap()
        .then((res) => {
          refetch();
          toast({
            variant: "success",
            title: "Ticket Deleted Successfully",
          });
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            title: `Failed to Delete Ticket.${error?.data?.detail || error?.data?.message}`,
          });
        });
      return;
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ?? (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const cancelRow = async () => {
    try {
      const resp = await cancelTicket({
        ticket_ids: [Number(data?.id)],
        cancel_reason: "cancelled from admin.",
        staffId: userInfo?.id,
      })
        .unwrap()
        .then((res) => {
          refetch();
          toast({
            variant: "success",
            title: "Ticket Cancelled Successfully",
          });
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            title: `${
              error?.data?.detail === "No eligible tickets found for update"
                ? "The ticket must be in pending status to cancel."
                : error?.data?.message ===
                    "No eligible tickets found for update"
                  ? "The ticket must be in pending status to cancel"
                  : error?.data?.message
            }`,
          });
        });
      return;
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ?? (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };


  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="">
            <DialogTrigger asChild>
              <DropdownMenuItem
                onClick={() => {
                  setTicketId(data?.id);
                  setOpenLog(true);
                }}
              >
                <TicketLogo className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
            </DialogTrigger>
            {/*
            // Original logic for showing print option:
            // Show print option if within 1 hour margin of scheduled time
            (() => {
              // Parse ticket's scheduled date and time
              const ticketDateTime = new Date(`${data.date}T${data.time}`);
              // Get current date and time
              const now = new Date();
              // Calculate difference in milliseconds
              const diffMs = Math.abs(now.getTime() - ticketDateTime.getTime());
              // Convert to hours (1 hour = 60 * 60 * 1000 ms)
              const diffHours = diffMs / (1000 * 60 * 60);

              // Show print option if within 1 hour margin
              return diffHours <= 1;
            })()
            */}
            {/* New logic: Display print button if data.date is today */}
            {data.date === new Date().toISOString().split('T')[0] &&
              <DialogTrigger asChild>
                <DropdownMenuItem
                  onClick={() => {
                    handleBrowserPrint(data)
                  }}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print
                </DropdownMenuItem>
              </DialogTrigger>
            }

            {/* <DialogTrigger asChild>
              <DropdownMenuItem
                onClick={() => {
                  refetch();
                  setSheetOpen(true);
                  setSelectedTicket(data);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Ticket
              </DropdownMenuItem>
            </DialogTrigger> */}

            {access == "write" &&
              [
                TicketStatus.NEW,
                TicketStatus.CANCELLED,
                TicketStatus.HOLD,
                TicketStatus.NO_SHOW,
              ].includes(data?.status) &&
              new Date(data?.created_at).toDateString() !==
                new Date().toDateString() && (
                <DropdownMenuItem onClick={() => setIsDelete(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}

            {access == "write" &&
              [TicketStatus.NEW].includes(data?.status) &&
              new Date(data?.created_at).toDateString() !==
                new Date().toDateString() && (
                <DropdownMenuItem onClick={() => setIsCancel(true)}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </DropdownMenuItem>
              )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Dialog>
      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={() => setIsDelete(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-start  justify-start gap-4">
                  <img src={warning} alt="warning" className="size-14" />
                  <AlertDialogTitle className="text-lg font-medium">
                    Are you sure you want to delete this Ticket? This action
                    cannot be undone.
                  </AlertDialogTitle>
                </div>
                <div className=" flex justify-end items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={() => setIsDelete(false)}
                    className="w-[100px] border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteRow}
                    className="w-[100px] bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {iscancel && (
        <AlertDialog open={iscancel} onOpenChange={() => setIsCancel(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-start  justify-start gap-4">
                  <img src={warning} alt="warning" className="size-14" />
                  <AlertDialogTitle className="text-lg font-medium">
                    Are you sure you want to cancel this Ticket? This action
                    cannot be undone.
                  </AlertDialogTitle>
                </div>
                <div className=" flex justify-end items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={() => setIsCancel(false)}
                    className="w-[100px] border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    No
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={cancelRow}
                    className="w-[100px] bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Yes
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
